import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import ParentHeader from "../Parent/ParentHeader/ParentHeader";
import { Msr, useMaterialSymbols } from "../Teacher/Oireachtas/components";
import { LOGOUT } from "../redux/actionTypes/authActionType";
import { getRequest, payRequest } from "./api";
import { rememberPayPath } from "./payLink";
import { stripeAppearance } from "./stripeAppearance";
import "../Teacher/Oireachtas/oireachtas.css";
import "../Teacher/Oireachtas/t7.css";
import "../Teacher/Oireachtas/t8.css";
import "./payRequest.css";

// /pay/:requestId — the payer's side of an admin payment request (card
// 1bda37b5, PLAN.md §8). Everything renders from the server's `phase`
// (pay | processing | paid | canceled), never from local guesses: a refresh
// before confirming, an abandoned card sheet or a declined card all come back
// as `pay`, and POST /pay resumes the same PaymentIntent. The page owns its
// own <Elements> and never depends on router state (deep-linkable, refreshable).

export const POLL_MS = 2000;
export const POLL_LIMIT = 15; // 30 s
export const POLL_DEADLINE_MS = POLL_MS * POLL_LIMIT;

// Stripe.js is loaded per page (and again on Retry), never at import time, so
// a failed download shows a recovery state instead of hanging (review R1-F5).
const startStripe = () =>
  Promise.resolve()
    .then(() => loadStripe(process.env.REACT_APP_PUBLISHKEY))
    .then((stripe) => {
      if (!stripe) throw new Error("Stripe.js did not load");
      return stripe;
    });

export function formatMoney(cents, currency) {
  if (cents == null) return "—";
  const cur = String(currency || "cad").toUpperCase();
  try {
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: cur }).format(cents / 100);
  } catch (e) {
    return `${(cents / 100).toFixed(2)} ${cur}`;
  }
}

// dueAt is a calendar day (stored at UTC midnight); paidAt is an instant.
const dayLabel = (iso, opts) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric", ...opts });
};
const dueLabel = (iso) => dayLabel(iso, { weekday: "short", month: "short", year: undefined, timeZone: "UTC" });

const errCode = (e) => e && e.error && e.error.response && e.error.response.data && e.error.response.data.code;
const errText = (e) => {
  const m = e && e.messages;
  if (Array.isArray(m)) return m.join(" ");
  return typeof m === "string" && m !== "ServerError" ? m : "Something went wrong. Please try again.";
};

function Summary({ req }) {
  return (
    <div className="aside">
      <div className="pay">
        <h2>Summary</h2>
        <div className="prq-for">
          <div className="l">For</div>
          {req.note}
          {req.dueAt && (
            <div><span className="prq-due"><Msr>schedule</Msr>Please pay by {dueLabel(req.dueAt)}</span></div>
          )}
        </div>
        <div className="pay-body">
          <div className="prow">
            <span className="k">{req.organizerName}{req.eventName && <span className="subk">{req.eventName}</span>}</span>
            <span className="v">{formatMoney(req.organizerAmountCents, req.currency)}</span>
          </div>
          {req.feeCents > 0 && (
            <div className="prow"><span className="k">Platform fee</span><span className="v">{formatMoney(req.feeCents, req.currency)}</span></div>
          )}
          <div className="prow total">
            <span className="k">Total</span>
            <span className="v">{formatMoney(req.totalCents, req.currency)}<span className="cur">{String(req.currency || "cad").toUpperCase()}</span></span>
          </div>
        </div>
        <div className="paynote"><Msr>info</Msr>Your receipt is emailed as soon as the payment goes through. No refunds, as for all entries.</div>
      </div>
    </div>
  );
}

function CardForm({ req, id }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [loadFailed, setLoadFailed] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const { error: err } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${process.env.REACT_APP_BASE}/pay/${id}?done=1` },
    });
    // Only reached when Stripe could not redirect (card declined, validation).
    if (err) setError(err.message || "Your payment didn't go through. Please try again.");
    setBusy(false);
  };

  return (
    <form className="pcard" onSubmit={submit} aria-label="Card payment">
      <div className="pcard-h">
        <span className="nm">Card details</span>
        <div className="cardbrands">
          <span className="brand visa">VISA</span>
          <span className="brand mc">MC</span>
          <span className="brand amex">AMEX</span>
        </div>
      </div>
      <div className="pform">
        {error && <div className="guard-errors" role="alert">{error}</div>}
        {loadFailed && (
          <div className="guard-errors" role="alert">The card form couldn't load. Refresh the page to try again.</div>
        )}
        <PaymentElement onLoadError={() => setLoadFailed(true)} />
        <button type="submit" className="bigbtn prq-paybtn" disabled={!stripe || busy || loadFailed}>
          {busy ? <><span className="spinner"></span>Processing…</> : <><Msr>lock</Msr>Pay {formatMoney(req.totalCents, req.currency)}</>}
        </button>
        <div className="secureline">
          <Msr>verified_user</Msr>Your payment is encrypted and secure.
          <span className="stripe">Powered by <b>stripe</b></span>
        </div>
      </div>
      {(req.payerName || req.payerEmail) && (
        <div className="payer"><Msr>person</Msr>Paying as {[req.payerName, req.payerEmail].filter(Boolean).join(" · ")}</div>
      )}
    </form>
  );
}

// If Stripe's card form can't start (a bad or expired client secret, Stripe.js
// failing to load), show a way out instead of a blank page.
export class CardFormBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error) {
    console.error("[PayRequest] card form failed:", error && error.message);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="pcard prq-perr">
        <div className="guard-errors" role="alert">The card form couldn't load. Please try again.</div>
        <button type="button" className="bigbtn ghost" onClick={() => { this.setState({ failed: false }); this.props.onRetry(); }}>Try again</button>
      </div>
    );
  }
}

// Every piece of payment state belongs to ONE request: a different id mounts a
// fresh page (new state, new <Elements>), so a client secret can never be used
// under another request's summary or return URL (review R1-F2).
export default function PayRequest() {
  const { requestId } = useParams();
  return <PayRequestPage key={requestId} id={requestId} />;
}

function PayRequestPage({ id }) {
  useMaterialSymbols();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [view, setView] = React.useState({ status: "loading" });
  const [secret, setSecret] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [payError, setPayError] = React.useState(null);
  const [pollTimedOut, setPollTimedOut] = React.useState(false);
  // Each "Try again" bumps these, so the effect really runs again (R1-F1, R1-F5).
  const [payAttempt, setPayAttempt] = React.useState(0);
  const [stripeAttempt, setStripeAttempt] = React.useState(0);
  const [stripe, setStripe] = React.useState({ status: "idle", value: null });
  const startedFor = React.useRef(null);
  // POST /pay in flight, and a Retry waiting for its refresh: the "Preparing"
  // spinner shows only while one of these is true (never a dead end).
  const [starting, setStarting] = React.useState(false);
  const [retrying, setRetrying] = React.useState(false);
  // Stale-response guards (review R2-F1): only the newest load may set state,
  // and a load that started before a newer state (a POST /pay result, a poll)
  // is ignored when it finally resolves.
  const loadSeq = React.useRef(0);
  const stateVersion = React.useRef(0);
  const setViewNewer = React.useCallback((next) => {
    stateVersion.current += 1;
    setView(next);
  }, []);
  // Responses that arrive after this page is gone (another id, navigation) are ignored.
  const alive = React.useRef(true);
  React.useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const req = view.status === "ready" ? view.req : null;
  const phase = req ? req.phase : null;

  const load = React.useCallback(async () => {
    const seq = ++loadSeq.current;
    const version = stateVersion.current;
    const current = () => alive.current && seq === loadSeq.current && version === stateVersion.current;
    try {
      const res = await getRequest(id);
      if (!current()) return null;
      stateVersion.current += 1;
      if (res.forOtherAccount) { setView({ status: "other" }); return null; }
      setView({ status: "ready", req: res.request });
      return res.request;
    } catch (e) {
      if (current()) {
        stateVersion.current += 1;
        setView({ status: "error", message: errText(e) });
      }
      return null;
    }
  }, [id]);

  // Retry waits for the fresh status before starting another payment attempt,
  // and only starts one if the request is still waiting to be paid (R2-F1).
  const retryPayment = async () => {
    setPayError(null);
    setSecret(null);
    setRetrying(true);
    const fresh = await load();
    if (!alive.current) return;
    setRetrying(false);
    if (fresh && fresh.phase === "pay") setPayAttempt((n) => n + 1);
  };

  // First load. A Stripe return (?done=1&redirect_status=…) is read once, then
  // the query is dropped: the server's phase decides what to show.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get("redirect_status");
    const piSecret = params.get("payment_intent_client_secret");
    if (params.toString()) window.history.replaceState(null, "", window.location.pathname);
    (async () => {
      const r = await load();
      if (r && r.phase === "pay" && redirectStatus === "failed") {
        let message = "Your payment didn't go through. Please try again with another card.";
        try {
          const stripeJs = piSecret ? await startStripe() : null;
          const out = stripeJs ? await stripeJs.retrievePaymentIntent(piSecret) : null;
          const last = out && out.paymentIntent && out.paymentIntent.last_payment_error;
          if (last && last.message) message = last.message;
        } catch (e) { /* keep the generic message */ }
        if (alive.current) setNotice(message);
      }
    })();
  }, [load]);

  // pay: create or resume the PaymentIntent and mount the card form. Runs once
  // per attempt; "Try again" starts a new attempt (R1-F1).
  React.useEffect(() => {
    if (phase !== "pay" || secret || payError) return;
    if (startedFor.current === payAttempt) return; // StrictMode re-run of the same attempt
    startedFor.current = payAttempt;
    const attempt = payAttempt;
    setStarting(true);
    (async () => {
      try {
        const res = await payRequest(id);
        if (!alive.current || attempt !== startedFor.current) return;
        if (res.forOtherAccount) { setViewNewer({ status: "other" }); return; }
        if (res.alreadyPaid) { load(); return; }
        if (res.checkout && res.checkout.client_secret) setSecret(res.checkout.client_secret);
        else setPayError("We couldn't start the payment. Please try again.");
      } catch (e) {
        if (!alive.current || attempt !== startedFor.current) return;
        const code = errCode(e);
        if (code === "PAYMENT_PROCESSING") {
          // Newer than any refresh still in flight: an older 'pay' answer must
          // not pull the page back to a spinner (R2-F1).
          setViewNewer((v) => (v.status === "ready" ? { ...v, req: { ...v.req, phase: "processing" } } : v));
        } else if (code === "CANCELED") {
          load();
        } else {
          setPayError(errText(e));
        }
      } finally {
        if (alive.current && attempt === startedFor.current) setStarting(false);
      }
    })();
  }, [phase, secret, payError, payAttempt, id, load, setViewNewer]);

  // Stripe.js for the card form: loaded when there is a secret, again on Retry (R1-F5).
  React.useEffect(() => {
    if (!secret) return undefined;
    let current = true;
    setStripe({ status: "loading", value: null });
    startStripe()
      .then((value) => { if (current) setStripe({ status: "ready", value }); })
      .catch(() => { if (current) setStripe({ status: "failed", value: null }); });
    return () => { current = false; };
  }, [secret, stripeAttempt]);

  // processing: poll until paid (or the intent turns out to have failed). One
  // request in flight at a time, and an independent deadline, so a slow or
  // hanging request can't keep the page confirming forever (R1-F4).
  React.useEffect(() => {
    if (phase !== "processing") return undefined;
    let active = true;
    let timer = null;
    setPollTimedOut(false);
    const deadline = setTimeout(() => {
      active = false;
      clearTimeout(timer);
      if (alive.current) setPollTimedOut(true);
    }, POLL_DEADLINE_MS);
    const poll = async () => {
      let done = false;
      try {
        const res = await getRequest(id);
        if (!active) return;
        if (res.forOtherAccount) { setViewNewer({ status: "other" }); done = true; }
        else if (res.request.phase !== "processing") {
          const next = res.request;
          if (next.phase === "pay") {
            setSecret(null);
            setPayAttempt((n) => n + 1); // a fresh attempt resumes or replaces the intent
            setNotice("That payment didn't go through, so nothing was charged. Please try again.");
          }
          setViewNewer({ status: "ready", req: next });
          done = true;
        }
      } catch (e) { /* keep polling until the deadline */ }
      if (active && !done) timer = setTimeout(poll, POLL_MS);
    };
    timer = setTimeout(poll, POLL_MS);
    return () => { active = false; clearTimeout(timer); clearTimeout(deadline); };
  }, [phase, id, setViewNewer]);

  const switchAccount = () => {
    rememberPayPath(window.location.pathname);
    dispatch({ type: LOGOUT });
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  let body;
  if (view.status === "loading") {
    body = <div className="prq-msg" role="status"><span className="prq-spin" aria-hidden="true"></span><p>Loading…</p></div>;
  } else if (view.status === "error") {
    body = (
      <div className="prq-msg">
        <span className="ic"><Msr>error</Msr></span>
        <h1>We couldn't load this payment request</h1>
        <p>{view.message}</p>
        <button type="button" className="bigbtn ghost" onClick={load}>Try again</button>
      </div>
    );
  } else if (view.status === "other") {
    body = (
      <div className="prq-msg">
        <span className="ic amber"><Msr>no_accounts</Msr></span>
        <h1>This payment request is for a different account</h1>
        <p>You're logged in, but this link was sent to another Feis.link account. Sign out, then log in with the email address the request was sent to — you'll come straight back here.</p>
        <button type="button" className="bigbtn" onClick={switchAccount}><Msr>logout</Msr>Sign out and switch account</button>
        <p className="prq-small">Not sure which account? Reply to the email or write to info@feis.link.</p>
      </div>
    );
  } else if (phase === "canceled") {
    body = (
      <div className="prq-msg">
        <span className="ic"><Msr>block</Msr></span>
        <h1>This payment request was cancelled</h1>
        <p>There's nothing to pay on this link. If you think this is a mistake, write to info@feis.link.</p>
        <button type="button" className="bigbtn ghost" onClick={() => navigate("/parent")}>Back to Feis.link</button>
      </div>
    );
  } else if (phase === "processing") {
    body = pollTimedOut ? (
      <div className="prq-msg" role="status">
        <span className="ic amber"><Msr>hourglass_top</Msr></span>
        <h1>Your payment is being processed</h1>
        <p>We'll email your receipt as soon as it goes through. You don't need to pay again.</p>
        <button type="button" className="bigbtn ghost" onClick={() => navigate("/parent")}>Back to Feis.link</button>
      </div>
    ) : (
      <div className="prq-msg" role="status">
        <span className="prq-spin" aria-hidden="true"></span>
        <h1>Confirming your payment…</h1>
        <p>This usually takes a few seconds. Please keep this page open.</p>
      </div>
    );
  } else if (phase === "paid") {
    body = (
      <div className="ocfm">
        <div className="hero">
          <div className="check"><span className="ring"><Msr>check</Msr></span></div>
          <h1>Payment received</h1>
          <p className="lead">Thank you{req.payerName ? `, ${req.payerName.split(" ")[0]}` : ""}. {req.organizerName} has been paid.</p>
          <span className="emailed"><Msr>mark_email_read</Msr>A receipt has been emailed to you</span>
        </div>
        <div className="rcpt">
          <div className="rcpt-head">
            <div>
              <div className="rn">Receipt{req.receipt ? ` #${req.receipt}` : ""}</div>
              {req.paidAt && <div className="rd">Paid {dayLabel(req.paidAt)}</div>}
            </div>
            <span className="paid"><Msr>check_circle</Msr>Paid</span>
          </div>
          <div className="rcpt-meta">
            <div className="m"><div className="l">Organizer</div><div className="v">{req.organizerName}</div></div>
            <div className="m"><div className="l">Event</div><div className="v">{req.eventName}</div></div>
            <div className="m prq-wide"><div className="l">For</div><div className="v prq-plain">{req.note}</div></div>
          </div>
          <div className="rcpt-body">
            <div className="rrow"><span className="k">{req.organizerName}</span><span className="v">{formatMoney(req.organizerAmountCents, req.currency)}</span></div>
            {req.feeCents > 0 && <div className="rrow"><span className="k">Platform fee</span><span className="v">{formatMoney(req.feeCents, req.currency)}</span></div>}
            <div className="rrow total"><span className="k">Total paid</span><span className="v">{formatMoney(req.totalCents, req.currency)}<span className="cur">{String(req.currency || "cad").toUpperCase()}</span></span></div>
          </div>
        </div>
        <div className="actions">
          <button type="button" className="bigbtn ghost prq-center" onClick={() => navigate("/parent")}>Back to Feis.link</button>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="wrap narrow">
        <div className="prq-eyebrow">Payment request</div>
        <h1 className="ptitle">{req.eventName}</h1>
        <p className="prq-lead">From {req.organizerName}, sent by Feis.link.</p>
        {notice && <div className="guard-errors" role="alert">{notice}</div>}
        <div className="grid">
          <div>
            {payError ? (
              <div className="pcard prq-perr">
                <div className="guard-errors" role="alert">{payError}</div>
                <button type="button" className="bigbtn ghost" onClick={retryPayment}>Try again</button>
              </div>
            ) : secret && stripe.status === "failed" ? (
              <div className="pcard prq-perr">
                <div className="guard-errors" role="alert">The card form couldn't load. Please try again.</div>
                <button type="button" className="bigbtn ghost" onClick={() => setStripeAttempt((n) => n + 1)}>Try again</button>
              </div>
            ) : secret && stripe.status === "ready" ? (
              <CardFormBoundary onRetry={retryPayment}>
                <Elements key={secret} stripe={stripe.value} options={{ clientSecret: secret, appearance: stripeAppearance }}>
                  <CardForm req={req} id={id} />
                </Elements>
              </CardFormBoundary>
            ) : secret || starting || retrying || startedFor.current !== payAttempt ? (
              <div className="pcard prq-perr" role="status"><span className="prq-spin" aria-hidden="true"></span>Preparing secure payment…</div>
            ) : (
              // Nothing in flight and no card form: never leave a bare spinner.
              <div className="pcard prq-perr">
                <div className="guard-errors" role="alert">The payment didn't start. Please try again.</div>
                <button type="button" className="bigbtn ghost" onClick={retryPayment}>Try again</button>
              </div>
            )}
          </div>
          <Summary req={req} />
        </div>
      </div>
    );
  }

  return (
    <div className="oir opay prq">
      <ParentHeader />
      {body}
    </div>
  );
}
